import {AiVideoAudio, AiVideoQuality} from '@Panels/ai-video-panel/ai-video-panel.types';
import {stringToEnum} from '@Utils/string.util';
import type {PosterAnimationInProgress} from '@Components/poster-editor/components/poster-editing-side-panel/components/ai-poster-animation-panel/ai-poster-animation-panel.types';
import {
  DEFAULT_POSTER_ANIMATION_AUDIO,
  DEFAULT_POSTER_ANIMATION_QUALITY,
} from '@Components/poster-editor/components/poster-editing-side-panel/components/ai-poster-animation-panel/ai-poster-animation-panel.types';
import type {AiAnimationSourceType} from '@Libraries/ai-poster-animation.library';

const ON_MY_DESIGN_EXPANDED_STORAGE_KEY = 'pmw_color_picker_on_my_design_expanded';
const POSTER_ANIMATION_PREFERENCES_STORAGE_KEY = 'pmw_poster_animation_preferences';
/** Per-source: an item generation must not retire the page panel's finalize hint, or vice versa. */
const getPosterAnimationGenerationCountStorageKey = (sourceType: AiAnimationSourceType): string => `pmw_poster_animation_generation_count_${sourceType}`;
const IN_FLIGHT_POSTER_ANIMATIONS_STORAGE_KEY = 'pmw_in_flight_poster_animations';
/** The backend gives an animation job a fifteen minute deadline; past that there is nothing to rejoin. */
const IN_FLIGHT_POSTER_ANIMATION_MAX_AGE_MS = 15 * 60 * 1000;

export interface PosterAnimationPreferences {
  quality: AiVideoQuality;
  audio: AiVideoAudio;
}

export const getPersistedIsOnMyDesignExpanded = (): boolean => {
  try {
    return localStorage.getItem(ON_MY_DESIGN_EXPANDED_STORAGE_KEY) === 'true';
  } catch {
    // localStorage may be unavailable
  }
  return false;
};

export const persistIsOnMyDesignExpanded = (isExpanded: boolean): void => {
  try {
    localStorage.setItem(ON_MY_DESIGN_EXPANDED_STORAGE_KEY, String(isExpanded));
  } catch {
    // localStorage may be unavailable
  }
};

export const getPersistedPosterAnimationPreferences = (): PosterAnimationPreferences => {
  try {
    const stored = localStorage.getItem(POSTER_ANIMATION_PREFERENCES_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as {quality?: string; audio?: string};
      return {
        quality: stringToEnum(parsed.quality, Object.values(AiVideoQuality), DEFAULT_POSTER_ANIMATION_QUALITY),
        audio: stringToEnum(parsed.audio, Object.values(AiVideoAudio), DEFAULT_POSTER_ANIMATION_AUDIO),
      };
    }
  } catch {
    // localStorage may be unavailable
  }
  return {quality: DEFAULT_POSTER_ANIMATION_QUALITY, audio: DEFAULT_POSTER_ANIMATION_AUDIO};
};

export const persistPosterAnimationPreferences = (preferences: PosterAnimationPreferences): void => {
  try {
    localStorage.setItem(POSTER_ANIMATION_PREFERENCES_STORAGE_KEY, JSON.stringify(preferences));
  } catch {
    // localStorage may be unavailable
  }
};

// Seeds the finalize-hint visibility at first paint, before the async generations fetch resolves.
export const getPersistedPosterAnimationCount = (sourceType: AiAnimationSourceType): number | null => {
  try {
    const stored = localStorage.getItem(getPosterAnimationGenerationCountStorageKey(sourceType));
    if (stored !== null) {
      const parsed = Number.parseInt(stored, 10);
      if (Number.isFinite(parsed) && parsed >= 0) {
        return parsed;
      }
    }
  } catch {
    // localStorage may be unavailable
  }
  return null;
};

export const persistPosterAnimationCount = (sourceType: AiAnimationSourceType, count: number): void => {
  try {
    localStorage.setItem(getPosterAnimationGenerationCountStorageKey(sourceType), `${count}`);
  } catch {
    // localStorage may be unavailable
  }
};

/**
 * A generation runs to completion on the server whether or not the tab that started it is still
 * open, but its polling key is a random uuid minted per kickoff — nothing about it can be rederived
 * from the poster afterwards. Recording the key, with the shape its skeleton was drawn at, is what
 * lets a later page load rejoin the job instead of showing an idle panel until it happens to finish.
 */
export interface InFlightPosterAnimation extends PosterAnimationInProgress {
  pollingKey: string;
  /** Rejoining is scoped to the design the generation was started on; see rejoinPosterAnimationGeneration. */
  posterHashedId: string;
  startedAt: number;
}

const isInFlightPosterAnimation = (value: unknown): value is InFlightPosterAnimation => {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const candidate = value as Partial<InFlightPosterAnimation>;
  return (
    typeof candidate.id === 'string' &&
    typeof candidate.pollingKey === 'string' &&
    typeof candidate.posterHashedId === 'string' &&
    typeof candidate.width === 'number' &&
    typeof candidate.height === 'number' &&
    typeof candidate.startedAt === 'number' &&
    candidate.params !== undefined
  );
};

const writeInFlightPosterAnimations = (records: InFlightPosterAnimation[]): void => {
  try {
    localStorage.setItem(IN_FLIGHT_POSTER_ANIMATIONS_STORAGE_KEY, JSON.stringify(records));
  } catch {
    // localStorage may be unavailable
  }
};

/**
 * Entries past the backend's own deadline are dropped rather than returned: their job record is
 * gone, so the status endpoint would report PROCESSING forever and the caller would poll a job
 * that can never land.
 */
export const readInFlightPosterAnimations = (): InFlightPosterAnimation[] => {
  try {
    const stored = localStorage.getItem(IN_FLIGHT_POSTER_ANIMATIONS_STORAGE_KEY);
    if (stored === null) {
      return [];
    }
    const parsed: unknown = JSON.parse(stored);
    if (!Array.isArray(parsed)) {
      return [];
    }
    const oldestAllowedStartTime = Date.now() - IN_FLIGHT_POSTER_ANIMATION_MAX_AGE_MS;
    const live = parsed.filter(isInFlightPosterAnimation).filter((record) => record.startedAt > oldestAllowedStartTime);
    if (live.length !== parsed.length) {
      writeInFlightPosterAnimations(live);
    }
    return live;
  } catch {
    // localStorage may be unavailable, or hold something this build cannot read
  }
  return [];
};

export const addInFlightPosterAnimation = (record: InFlightPosterAnimation): void => {
  writeInFlightPosterAnimations([...readInFlightPosterAnimations(), record]);
};

export const removeInFlightPosterAnimation = (id: string): void => {
  writeInFlightPosterAnimations(readInFlightPosterAnimations().filter((record) => record.id !== id));
};
